import React, { createContext, useContext, useState, useCallback } from 'react';
import { remediationRecords as initialRecords } from '../data/remediationRecords';
import { remediationRules } from '../data/remediationWorkflows';
import { learners as initialLearners } from '../data/learners';

const RemediationContext = createContext(null);

export const RemediationProvider = ({ children }) => {
  const [records, setRecords] = useState(initialRecords);
  const [learners, setLearners] = useState(initialLearners);
  const [notifications, setNotifications] = useState([]);
  const [activityLog, setActivityLog] = useState([]);

  const addLog = useCallback((entry) => {
    setActivityLog(prev => [{ ...entry, id: Date.now(), timestamp: new Date().toISOString() }, ...prev]);
  }, []);

  const triggerPhishingFailure = useCallback(({ learnerId, simulationId, simulationName, eventType }) => {
    const rule = remediationRules.find(r => r.triggerEvent === eventType);
    if (!rule) return null;

    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + rule.dueDays);

    const newRecord = {
      id: `REM${Date.now()}`,
      learnerId,
      simulationId,
      triggerEvent: eventType,
      triggeredAt: new Date().toISOString(),
      riskScoreDelta: rule.riskScoreDelta,
      assignedModuleId: rule.assignedModuleId,
      dueDate: dueDate.toISOString().split('T')[0],
      completionDate: null,
      status: 'Pending',
      notificationsSent: [
        ...(rule.notifyLearner ? ['learner'] : []),
        ...(rule.notifyManager ? ['manager'] : []),
        ...(rule.notifyAdmin ? ['admin'] : []),
      ],
      managerNotified: rule.notifyManager,
      adminNotified: rule.notifyAdmin,
      completionScore: null,
      riskAdjusted: false,
      riskAdjustmentDelta: 0,
    };

    setRecords(prev => [newRecord, ...prev]);

    // Update learner risk score
    setLearners(prev => prev.map(l =>
      l.id === learnerId
        ? { ...l, phishingRiskScore: Math.min(100, l.phishingRiskScore + rule.riskScoreDelta), trainingStatus: 'Overdue' }
        : l
    ));

    // Add notifications
    const notifBase = { learnerId, simulationName, eventType, recordId: newRecord.id, timestamp: new Date().toISOString() };
    const newNotifs = [];
    if (rule.notifyLearner) newNotifs.push({ ...notifBase, id: `N${Date.now()}a`, type: 'learner', read: false, message: `Remedial training assigned after phishing failure: ${simulationName}` });
    if (rule.notifyManager) newNotifs.push({ ...notifBase, id: `N${Date.now()}b`, type: 'manager', read: false, message: `Learner ${learnerId} submitted credentials in "${simulationName}". Action required.` });
    if (rule.notifyAdmin) newNotifs.push({ ...notifBase, id: `N${Date.now()}c`, type: 'admin', read: false, message: `HIGH RISK: Credential submission detected in "${simulationName}" — remediation auto-assigned.` });
    setNotifications(prev => [...newNotifs, ...prev]);

    addLog({ type: 'failure', learnerId, simulationName, eventType, severity: rule.severity, message: `Phishing failure detected: ${eventType} on "${simulationName}"` });
    addLog({ type: 'assignment', learnerId, message: `Remedial module auto-assigned. Due: ${newRecord.dueDate}` });
    if (rule.notifyManager) addLog({ type: 'notification', learnerId, message: 'Manager notified via email' });
    if (rule.notifyAdmin) addLog({ type: 'notification', learnerId, message: 'Security admin alerted' });

    return newRecord;
  }, [addLog]);

  const completeRemediation = useCallback((recordId, score) => {
    const delta = score >= 80 ? -10 : score >= 60 ? -5 : 0;
    setRecords(prev => prev.map(r =>
      r.id === recordId
        ? { ...r, status: 'Completed', completionDate: new Date().toISOString().split('T')[0], completionScore: score, riskAdjusted: delta !== 0, riskAdjustmentDelta: delta }
        : r
    ));
    const rec = records.find(r => r.id === recordId);
    if (rec && delta !== 0) {
      setLearners(prev => prev.map(l =>
        l.id === rec.learnerId
          ? { ...l, phishingRiskScore: Math.max(0, l.phishingRiskScore + delta), trainingStatus: score >= 80 ? 'Compliant' : 'In Progress' }
          : l
      ));
    }
    addLog({ type: 'completion', learnerId: rec?.learnerId, message: `Remediation completed. Score: ${score}%. Risk adjusted by ${delta}pts.` });
  }, [records, addLog]);

  const updateRecordStatus = useCallback((recordId, status) => {
    setRecords(prev => prev.map(r => r.id === recordId ? { ...r, status } : r));
  }, []);

  const markNotificationRead = useCallback((notifId) => {
    setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
  }, []);

  return (
    <RemediationContext.Provider value={{
      records, learners, notifications, activityLog,
      triggerPhishingFailure, completeRemediation, updateRecordStatus, markNotificationRead,
    }}>
      {children}
    </RemediationContext.Provider>
  );
};

export const useRemediation = () => {
  const ctx = useContext(RemediationContext);
  if (!ctx) throw new Error('useRemediation must be used within RemediationProvider');
  return ctx;
};